/**
 * DoraMetricsStore - MobX store for calculating DORA metrics
 *
 * This store computes the four key DORA metrics:
 * 1. Deployment Frequency - How often we release to production
 * 2. Lead Time for Changes - Time from commit to production
 * 3. Change Failure Rate - Percentage of deployments causing failures
 * 4. Time to Restore Service - Time to recover from failures
 *
 * Data sources:
 * - Releases data for deployment frequency
 * - Commits and PRs for lead time calculations
 * - Commit types (bugfix, revert) for failure estimation
 */

import { makeAutoObservable, runInAction } from "mobx"
import { fetchJson } from "../lib/fetch"
import { logger } from "../lib/logger"

// Constants for time calculations
const SECONDS_PER_DAY = 86400

// Translation keys for missing data messages
export type DoraMissingDataKey =
  | "doraMissingReleases"
  | "doraMissingPullRequests"
  | "doraMissingCommits"
  | "doraMissingDeployments"
  | "doraMissingWorkflowRuns"
  | "doraMissingIncidents"

// SQLite exports from backend
export interface ReleaseData {
  id: number
  repo_id: number
  tag_name: string
  name?: string
  author_id: number
  created_at: number // Unix timestamp
  published_at?: number // Unix timestamp
  draft: boolean
  prerelease: boolean
}

export interface PullRequestData {
  id: number
  repo_id: number
  number: number
  title: string
  creator_id: number
  created_at: number
  merged_at?: number
  closed_at?: number
  state: string
  draft: boolean
  additions: number
  deletions: number
  changed_files: number
  comments: number
}

export interface CommitData {
  hash: string
  repo_id: number
  author_id: number
  timestamp: number
  score: number
  class: string
  commit_type?: string
  message: string
  files_changed: number
  insertions: number
  deletions: number
  functions_changed: number
  cyclomatic_delta: number
  max_nesting_delta: number
}

export interface RepositoryData {
  id: number
  owner: string
  repo: string
  full_name: string
}

// Phase 2: Workflow and Deployment data
export interface WorkflowRunData {
  id: number
  repo_id: number
  workflow_id: number
  workflow_name: string
  commit_sha: string
  branch: string
  event: string
  status: string
  conclusion?: string
  run_number: number
  created_at: number
  updated_at: number
  run_started_at?: number
  run_completed_at?: number
  run_duration_seconds?: number
  actor_id?: number
}

export interface DeploymentData {
  id: number
  repo_id: number
  commit_sha: string
  ref: string
  environment: string
  description?: string
  creator_id?: number
  created_at: number
  updated_at: number
}

export interface DeploymentStatusData {
  id: number
  deployment_id: number
  state: string
  description?: string
  environment?: string
  created_at: number
  updated_at: number
}

// Phase 3: Incident tracking data
export interface IssueData {
  id: number
  repo_id: number
  number: number
  title: string
  creator_id: number
  created_at: number
  closed_at?: number
  closed_by_id?: number
  state: string
  comments: number
  comment_authors?: number
  first_comment_at?: number
  last_comment_at?: number
  // Phase 3 fields
  is_incident?: boolean
  incident_severity?: string
  incident_detected_at?: number
  incident_resolved_at?: number
  labels?: string
}

// Phase 4: Pre-calculated DORA metrics from backend
export interface DoraMetricData {
  id?: number
  repo_id: number
  author_id?: number | null // NULL for repo-level metrics, undefined if not provided
  period_type: "day" | "week" | "month"
  period_start: number // Unix timestamp
  period_end: number // Unix timestamp

  // Deployment Frequency
  deployment_count: number
  deployments_per_day: number

  // Lead Time for Changes
  lead_time_avg_hours?: number
  lead_time_median_hours?: number
  lead_time_p95_hours?: number
  lead_time_samples: number

  // Change Failure Rate (repo-level only)
  total_deployments?: number
  failed_deployments?: number
  failure_rate_percent?: number

  // Time to Restore (repo-level only)
  incident_count?: number
  mttr_avg_hours?: number
  mttr_median_hours?: number

  calculated_at: number // Unix timestamp
}

// Compact format for DORA metrics (optimized for file size)
// Field names are shortened to reduce JSON payload size
interface CompactDoraMetric {
  r: number // repo_id
  a?: number // author_id (optional)
  p: "day" | "week" | "month" // period_type
  s: number // period_start
  e: number // period_end
  dc: number // deployment_count
  dpd: number // deployments_per_day
  lta?: number // lead_time_avg_hours
  ltm?: number // lead_time_median_hours
  ltp?: number // lead_time_p95_hours
  lts: number // lead_time_samples (required, defaults to 0)
  td?: number // total_deployments
  fd?: number // failed_deployments
  fr?: number // failure_rate_percent
  ic?: number // incident_count
  ma?: number // mttr_avg_hours
  mm?: number // mttr_median_hours
  c: number // calculated_at
}

/**
 * Convert compact DORA metric to full format
 */
function expandCompactMetric(compact: CompactDoraMetric): DoraMetricData {
  return {
    repo_id: compact.r,
    author_id: compact.a ?? null,
    period_type: compact.p,
    period_start: compact.s,
    period_end: compact.e,
    deployment_count: compact.dc,
    deployments_per_day: compact.dpd,
    lead_time_avg_hours: compact.lta,
    lead_time_median_hours: compact.ltm,
    lead_time_p95_hours: compact.ltp,
    lead_time_samples: compact.lts ?? 0,
    total_deployments: compact.td,
    failed_deployments: compact.fd,
    failure_rate_percent: compact.fr,
    incident_count: compact.ic,
    mttr_avg_hours: compact.ma,
    mttr_median_hours: compact.mm,
    calculated_at: compact.c,
  }
}

// Computed metrics
export interface DeploymentFrequency {
  period: string // e.g., "2024-11"
  count: number
  deploymentsPerDay: number
}

export interface LeadTimeMetric {
  period: string
  averageHours: number
  medianHours: number
  p95Hours: number
}

export interface ChangeFailureMetric {
  period: string
  totalDeployments: number
  failedDeployments: number
  failureRate: number // percentage
}

export interface TimeToRestoreMetric {
  period: string
  averageHours: number
  medianHours: number
  incidents: number
}

export interface DoraPerformanceLevel {
  deploymentFrequency: "elite" | "high" | "medium" | "low"
  leadTime: "elite" | "high" | "medium" | "low"
  changeFailureRate: "elite" | "high" | "medium" | "low"
  timeToRestore: "elite" | "high" | "medium" | "low"
  overall: "elite" | "high" | "medium" | "low"
}

export class DoraMetricsStore {
  // Loading state
  isLoading = false
  loadError: string | null = null

  // Pre-calculated DORA metrics (Phase 4)
  doraMetrics: DoraMetricData[] = []

  // Legacy data (kept for backward compatibility and fallback)
  releases: ReleaseData[] = []
  pullRequests: PullRequestData[] = []
  commits: CommitData[] = []
  repositories: RepositoryData[] = []

  // Phase 2 & 3 data (deprecated - used only as fallback)
  workflowRuns: WorkflowRunData[] = []
  deployments: DeploymentData[] = []
  deploymentStatuses: DeploymentStatusData[] = []
  issues: IssueData[] = []

  // Time range filter (default: last 28 days / 4 weeks - matches backend retention)
  startDate: Date = new Date(Date.now() - 28 * 24 * 60 * 60 * 1000)
  endDate: Date = new Date()

  // Period type filter (weekly or monthly view)
  periodType: "week" | "month" = "month"

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Load data from backend exports
   *
   * This method now only loads the pre-calculated DORA metrics from dora_metrics.json.
   * Legacy fallback data files (releases.json, commits.json, etc.) are no longer loaded
   * as they are not available in the current deployment. All DORA metrics should be
   * pre-calculated by the backend and provided in dora_metrics.json.
   */
  async initialize(): Promise<void> {
    if (this.isLoading) {
      logger.debug("DoraMetricsStore already initializing, skipping", {
        component: "DoraMetricsStore",
      })
      return
    }

    try {
      runInAction(() => {
        this.isLoading = true
        this.loadError = null
      })

      logger.info("Initializing DoraMetricsStore...", {
        component: "DoraMetricsStore",
      })

      // Load pre-calculated DORA metrics (Phase 4)
      // This is the primary and only data source - all metrics should be pre-calculated by the backend
      const doraMetricsRaw = await fetchJson<(DoraMetricData | CompactDoraMetric)[]>(
        "/data/dora_metrics.json",
      ).catch((error) => {
        logger.warn("Failed to load dora_metrics.json - DORA metrics not available", {
          component: "DoraMetricsStore",
          error,
        })
        return [] as (DoraMetricData | CompactDoraMetric)[]
      })

      // Expand compact format if needed
      const doraMetrics = doraMetricsRaw.map((metric) => {
        // Check if this is compact format (has 'r' field instead of 'repo_id')
        if ("r" in metric) {
          return expandCompactMetric(metric as CompactDoraMetric)
        }
        return metric as DoraMetricData
      })

      runInAction(() => {
        this.doraMetrics = doraMetrics
      })

      logger.info("DoraMetricsStore initialized successfully", {
        component: "DoraMetricsStore",
        doraMetricsCount: doraMetrics.length,
        usingPreCalculatedMetrics: doraMetrics.length > 0,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize DoraMetricsStore"

      runInAction(() => {
        this.loadError = errorMessage
      })

      logger.error("Failed to initialize DoraMetricsStore", error, {
        component: "DoraMetricsStore",
      })

      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Set time range for metrics calculation
   */
  setTimeRange(startDate: Date, endDate: Date): void {
    this.startDate = startDate
    this.endDate = endDate
  }

  /**
   * Set period type for metrics aggregation
   */
  setPeriodType(periodType: "week" | "month"): void {
    this.periodType = periodType
  }

  /**
   * Format a period key based on period type
   * For months: "2024-11"
   * For weeks: "2024-W45" (ISO week number)
   */
  private formatPeriodKey(date: Date, periodType: "day" | "week" | "month"): string {
    if (periodType === "month") {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    } else if (periodType === "week") {
      // Calculate ISO week number
      const tempDate = new Date(date.getTime())
      tempDate.setHours(0, 0, 0, 0)
      // Set to nearest Thursday: current date + 4 - current day number
      tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7))
      // Get first day of year
      const yearStart = new Date(tempDate.getFullYear(), 0, 1)
      // Calculate full weeks to nearest Thursday
      const weekNo = Math.ceil(((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
      return `${tempDate.getFullYear()}-W${String(weekNo).padStart(2, "0")}`
    } else {
      // day
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    }
  }

  /**
   * Get filtered pre-calculated DORA metrics within time range
   * Filters by selected period type (week or month) and time range
   * When periodType is "week", aggregates daily metrics into weekly metrics
   */
  get filteredDoraMetrics(): DoraMetricData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    // If requesting weekly data, aggregate from daily data
    if (this.periodType === "week") {
      // Get all daily metrics in the time range
      const dailyMetrics = this.doraMetrics.filter(
        (metric) =>
          metric.period_end >= startTimestamp &&
          metric.period_start <= endTimestamp &&
          metric.period_type === "day" &&
          (metric.author_id === undefined || metric.author_id === null),
      )

      // Group daily metrics by ISO week
      const weeklyGroups = new Map<string, DoraMetricData[]>()

      for (const metric of dailyMetrics) {
        const date = new Date(metric.period_start * 1000)
        const period = this.formatPeriodKey(date, "week")

        if (!weeklyGroups.has(period)) {
          weeklyGroups.set(period, [])
        }
        weeklyGroups.get(period)!.push(metric)
      }

      // Aggregate each week's metrics
      const weeklyMetrics: DoraMetricData[] = []

      for (const [, metrics] of weeklyGroups.entries()) {
        if (metrics.length === 0) continue

        // Find min period_start and max period_end for this week
        const period_start = Math.min(...metrics.map((m) => m.period_start))
        const period_end = Math.max(...metrics.map((m) => m.period_end))

        // Aggregate deployment counts
        const deployment_count = metrics.reduce((sum, m) => sum + m.deployment_count, 0)
        const total_days = (period_end - period_start) / SECONDS_PER_DAY
        const deployments_per_day = total_days > 0 ? deployment_count / total_days : 0

        // Aggregate lead time metrics (weighted by samples)
        const metricsWithLeadTime = metrics.filter((m) => m.lead_time_samples > 0)
        let lead_time_avg_hours: number | undefined
        let lead_time_median_hours: number | undefined
        let lead_time_p95_hours: number | undefined
        let lead_time_samples = 0

        if (metricsWithLeadTime.length > 0) {
          let totalWeightedAvg = 0
          let totalWeightedMedian = 0
          let totalWeightedP95 = 0

          for (const m of metricsWithLeadTime) {
            const samples = m.lead_time_samples || 0
            totalWeightedAvg += (m.lead_time_avg_hours || 0) * samples
            totalWeightedMedian += (m.lead_time_median_hours || 0) * samples
            totalWeightedP95 += (m.lead_time_p95_hours || 0) * samples
            lead_time_samples += samples
          }

          if (lead_time_samples > 0) {
            lead_time_avg_hours = totalWeightedAvg / lead_time_samples
            lead_time_median_hours = totalWeightedMedian / lead_time_samples
            lead_time_p95_hours = totalWeightedP95 / lead_time_samples
          }
        }

        // Aggregate change failure rate
        const metricsWithFailureRate = metrics.filter(
          (m) => m.total_deployments !== undefined && m.total_deployments > 0,
        )
        let total_deployments: number | undefined
        let failed_deployments: number | undefined
        let failure_rate_percent: number | undefined

        if (metricsWithFailureRate.length > 0) {
          total_deployments = metricsWithFailureRate.reduce(
            (sum, m) => sum + (m.total_deployments || 0),
            0,
          )
          failed_deployments = metricsWithFailureRate.reduce(
            (sum, m) => sum + (m.failed_deployments || 0),
            0,
          )
          failure_rate_percent =
            total_deployments > 0 ? (failed_deployments / total_deployments) * 100 : 0
        }

        // Aggregate time to restore (weighted by incident count)
        const metricsWithIncidents = metrics.filter(
          (m) => m.incident_count !== undefined && m.incident_count > 0,
        )
        let incident_count: number | undefined
        let mttr_avg_hours: number | undefined
        let mttr_median_hours: number | undefined

        if (metricsWithIncidents.length > 0) {
          let totalWeightedAvg = 0
          let totalWeightedMedian = 0
          incident_count = 0

          for (const m of metricsWithIncidents) {
            const incidents = m.incident_count || 0
            totalWeightedAvg += (m.mttr_avg_hours || 0) * incidents
            totalWeightedMedian += (m.mttr_median_hours || 0) * incidents
            incident_count += incidents
          }

          if (incident_count > 0) {
            mttr_avg_hours = totalWeightedAvg / incident_count
            mttr_median_hours = totalWeightedMedian / incident_count
          }
        }

        // Use first metric's repo_id and most recent calculated_at
        const repo_id = metrics[0].repo_id
        const calculated_at = Math.max(...metrics.map((m) => m.calculated_at))

        weeklyMetrics.push({
          repo_id,
          author_id: null,
          period_type: "week",
          period_start,
          period_end,
          deployment_count,
          deployments_per_day,
          lead_time_avg_hours,
          lead_time_median_hours,
          lead_time_p95_hours,
          lead_time_samples,
          total_deployments,
          failed_deployments,
          failure_rate_percent,
          incident_count,
          mttr_avg_hours,
          mttr_median_hours,
          calculated_at,
        })
      }

      return weeklyMetrics.sort((a, b) => a.period_start - b.period_start)
    }

    // For month or day, return as-is
    return this.doraMetrics.filter(
      (metric) =>
        // Check if the metric period overlaps with the selected time range
        metric.period_end >= startTimestamp &&
        metric.period_start <= endTimestamp &&
        metric.period_type === this.periodType && // Filter by selected period type
        (metric.author_id === undefined || metric.author_id === null), // Repo-level metrics only
    )
  }

  /**
   * Get filtered per-author DORA metrics within time range
   */
  getFilteredDoraMetricsByAuthor(authorId?: number): DoraMetricData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.doraMetrics.filter(
      (metric) =>
        // Check if the metric period overlaps with the selected time range
        metric.period_end >= startTimestamp &&
        metric.period_start <= endTimestamp &&
        (authorId === undefined || metric.author_id === authorId),
    )
  }

  /**
   * Get filtered DORA metrics for a list of authors (used for team filtering)
   * Aggregates metrics across all authors in the team
   */
  getFilteredDoraMetricsByAuthorList(authorIds: number[]): DoraMetricData[] {
    if (authorIds.length === 0) {
      return this.filteredDoraMetrics // Return org-level metrics if no authors
    }

    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    // Get all metrics for the specified authors in the time range
    const authorMetrics = this.doraMetrics.filter(
      (metric) =>
        metric.period_end >= startTimestamp &&
        metric.period_start <= endTimestamp &&
        metric.author_id !== null &&
        metric.author_id !== undefined &&
        authorIds.includes(metric.author_id),
    )

    // If requesting weekly view, we need to aggregate daily metrics
    if (this.periodType === "week") {
      // Group by period (week)
      const weeklyGroups = new Map<string, DoraMetricData[]>()

      // First, get all daily metrics for these authors
      const dailyMetrics = this.doraMetrics.filter(
        (metric) =>
          metric.period_end >= startTimestamp &&
          metric.period_start <= endTimestamp &&
          metric.period_type === "day" &&
          metric.author_id !== null &&
          metric.author_id !== undefined &&
          authorIds.includes(metric.author_id),
      )

      for (const metric of dailyMetrics) {
        const date = new Date(metric.period_start * 1000)
        const weekKey = this.formatPeriodKey(date, "week")

        if (!weeklyGroups.has(weekKey)) {
          weeklyGroups.set(weekKey, [])
        }
        weeklyGroups.get(weekKey)!.push(metric)
      }

      // Aggregate each week's metrics
      const weeklyMetrics: DoraMetricData[] = []

      for (const [, metrics] of weeklyGroups.entries()) {
        if (metrics.length === 0) continue

        const period_start = Math.min(...metrics.map((m) => m.period_start))
        const period_end = Math.max(...metrics.map((m) => m.period_end))
        const deployment_count = metrics.reduce((sum, m) => sum + m.deployment_count, 0)
        const total_days = (period_end - period_start) / SECONDS_PER_DAY
        const deployments_per_day = total_days > 0 ? deployment_count / total_days : 0

        // Aggregate lead time metrics (weighted by samples)
        const metricsWithLeadTime = metrics.filter((m) => m.lead_time_samples > 0)
        let lead_time_avg_hours: number | undefined
        let lead_time_median_hours: number | undefined
        let lead_time_p95_hours: number | undefined
        let lead_time_samples = 0

        if (metricsWithLeadTime.length > 0) {
          let totalWeightedAvg = 0
          let totalWeightedMedian = 0
          let totalWeightedP95 = 0

          for (const m of metricsWithLeadTime) {
            const samples = m.lead_time_samples || 0
            totalWeightedAvg += (m.lead_time_avg_hours || 0) * samples
            totalWeightedMedian += (m.lead_time_median_hours || 0) * samples
            totalWeightedP95 += (m.lead_time_p95_hours || 0) * samples
            lead_time_samples += samples
          }

          if (lead_time_samples > 0) {
            lead_time_avg_hours = totalWeightedAvg / lead_time_samples
            lead_time_median_hours = totalWeightedMedian / lead_time_samples
            lead_time_p95_hours = totalWeightedP95 / lead_time_samples
          }
        }

        weeklyMetrics.push({
          repo_id: metrics[0].repo_id,
          author_id: null, // Aggregated across authors
          period_type: "week",
          period_start,
          period_end,
          deployment_count,
          deployments_per_day,
          lead_time_avg_hours,
          lead_time_median_hours,
          lead_time_p95_hours,
          lead_time_samples,
          calculated_at: Math.max(...metrics.map((m) => m.calculated_at)),
        })
      }

      return weeklyMetrics.sort((a, b) => a.period_start - b.period_start)
    }

    // For month view, filter by period_type and aggregate across authors
    const periodGroups = new Map<string, DoraMetricData[]>()

    const periodMetrics = authorMetrics.filter((m) => m.period_type === this.periodType)

    for (const metric of periodMetrics) {
      const date = new Date(metric.period_start * 1000)
      const periodKey = this.formatPeriodKey(date, this.periodType)

      if (!periodGroups.has(periodKey)) {
        periodGroups.set(periodKey, [])
      }
      periodGroups.get(periodKey)!.push(metric)
    }

    // Aggregate each period's metrics across authors
    const aggregatedMetrics: DoraMetricData[] = []

    for (const [, metrics] of periodGroups.entries()) {
      if (metrics.length === 0) continue

      const period_start = Math.min(...metrics.map((m) => m.period_start))
      const period_end = Math.max(...metrics.map((m) => m.period_end))
      const deployment_count = metrics.reduce((sum, m) => sum + m.deployment_count, 0)
      const total_days = (period_end - period_start) / SECONDS_PER_DAY
      const deployments_per_day = total_days > 0 ? deployment_count / total_days : 0

      // Aggregate lead time metrics (weighted by samples)
      const metricsWithLeadTime = metrics.filter((m) => m.lead_time_samples > 0)
      let lead_time_avg_hours: number | undefined
      let lead_time_median_hours: number | undefined
      let lead_time_p95_hours: number | undefined
      let lead_time_samples = 0

      if (metricsWithLeadTime.length > 0) {
        let totalWeightedAvg = 0
        let totalWeightedMedian = 0
        let totalWeightedP95 = 0

        for (const m of metricsWithLeadTime) {
          const samples = m.lead_time_samples || 0
          totalWeightedAvg += (m.lead_time_avg_hours || 0) * samples
          totalWeightedMedian += (m.lead_time_median_hours || 0) * samples
          totalWeightedP95 += (m.lead_time_p95_hours || 0) * samples
          lead_time_samples += samples
        }

        if (lead_time_samples > 0) {
          lead_time_avg_hours = totalWeightedAvg / lead_time_samples
          lead_time_median_hours = totalWeightedMedian / lead_time_samples
          lead_time_p95_hours = totalWeightedP95 / lead_time_samples
        }
      }

      aggregatedMetrics.push({
        repo_id: metrics[0].repo_id,
        author_id: null, // Aggregated across authors
        period_type: this.periodType,
        period_start,
        period_end,
        deployment_count,
        deployments_per_day,
        lead_time_avg_hours,
        lead_time_median_hours,
        lead_time_p95_hours,
        lead_time_samples,
        calculated_at: Math.max(...metrics.map((m) => m.calculated_at)),
      })
    }

    return aggregatedMetrics.sort((a, b) => a.period_start - b.period_start)
  }

  /**
   * Check if pre-calculated metrics are available and recent
   */
  get hasPreCalculatedMetrics(): boolean {
    return this.doraMetrics.length > 0
  }

  /**
   * Get the timestamp when metrics were last calculated
   */
  get metricsCalculatedAt(): Date | null {
    if (this.doraMetrics.length === 0) return null

    // Get the most recent calculated_at timestamp
    const latestTimestamp = Math.max(...this.doraMetrics.map((m) => m.calculated_at))
    return new Date(latestTimestamp * 1000)
  }

  /**
   * Get filtered releases within time range
   */
  get filteredReleases(): ReleaseData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.releases.filter((release) => {
      const timestamp = release.published_at || release.created_at
      return (
        timestamp >= startTimestamp &&
        timestamp <= endTimestamp &&
        !release.draft &&
        !release.prerelease
      )
    })
  }

  /**
   * Get filtered commits within time range
   */
  get filteredCommits(): CommitData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.commits.filter(
      (commit) => commit.timestamp >= startTimestamp && commit.timestamp <= endTimestamp,
    )
  }

  /**
   * Get filtered PRs within time range
   */
  get filteredPullRequests(): PullRequestData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.pullRequests.filter((pr) => {
      const timestamp = pr.merged_at || pr.created_at
      return timestamp >= startTimestamp && timestamp <= endTimestamp && pr.state === "closed"
    })
  }

  /**
   * Get filtered deployments within time range (Phase 2)
   */
  get filteredDeployments(): DeploymentData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.deployments.filter(
      (deployment) =>
        deployment.created_at >= startTimestamp &&
        deployment.created_at <= endTimestamp &&
        deployment.environment === "production", // Only count production deployments
    )
  }

  /**
   * Get filtered workflow runs within time range (Phase 2)
   */
  get filteredWorkflowRuns(): WorkflowRunData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.workflowRuns.filter(
      (run) =>
        run.created_at >= startTimestamp &&
        run.created_at <= endTimestamp &&
        run.status === "completed",
    )
  }

  /**
   * Get filtered incidents within time range (Phase 3)
   */
  get filteredIncidents(): IssueData[] {
    const startTimestamp = Math.floor(this.startDate.getTime() / 1000)
    const endTimestamp = Math.floor(this.endDate.getTime() / 1000)

    return this.issues.filter(
      (issue) =>
        issue.is_incident &&
        issue.incident_detected_at &&
        issue.incident_detected_at >= startTimestamp &&
        issue.incident_detected_at <= endTimestamp,
    )
  }

  /**
   * Get deployment success/failure status from deployment_statuses
   */
  private getDeploymentStatus(deploymentId: number): "success" | "failure" | "pending" | "unknown" {
    const statuses = this.deploymentStatuses
      .filter((s) => s.deployment_id === deploymentId)
      .sort((a, b) => b.created_at - a.created_at)

    if (statuses.length === 0) return "unknown"

    const latestStatus = statuses[0]
    if (latestStatus.state === "success") return "success"
    if (latestStatus.state === "failure" || latestStatus.state === "error") return "failure"
    return "pending"
  }

  /**
   * Calculate deployment frequency by period (week or month)
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2/releases
   *
   * Note: When multiple repositories are tracked, metrics are aggregated by period.
   * Each repository may have metrics for the same period, which are summed together.
   */
  get deploymentFrequency(): DeploymentFrequency[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      // filteredDoraMetrics already filters by selected period type

      if (metrics.length > 0) {
        // Aggregate metrics by period across all repos
        const periodMap = new Map<string, { count: number; totalDays: number }>()

        for (const metric of metrics) {
          const date = new Date(metric.period_start * 1000)
          // Format period based on period type
          const period = this.formatPeriodKey(date, metric.period_type)

          const existing = periodMap.get(period) || { count: 0, totalDays: 0 }
          const durationDays = (metric.period_end - metric.period_start) / SECONDS_PER_DAY

          periodMap.set(period, {
            count: existing.count + metric.deployment_count,
            totalDays: existing.totalDays + durationDays,
          })
        }

        // Convert map to sorted array
        return Array.from(periodMap.entries())
          .map(([period, data]) => ({
            period,
            count: data.count,
            deploymentsPerDay: data.totalDays > 0 ? data.count / data.totalDays : 0,
          }))
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const frequencyMap = new Map<string, number>()

    // Prefer deployment data (Phase 2) over releases
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Use actual deployment events
      for (const deployment of this.filteredDeployments) {
        const timestamp = deployment.created_at * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        frequencyMap.set(period, (frequencyMap.get(period) || 0) + 1)
      }
    } else {
      // Fallback: Use releases as proxy for deployments
      for (const release of this.filteredReleases) {
        const timestamp = (release.published_at || release.created_at) * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        frequencyMap.set(period, (frequencyMap.get(period) || 0) + 1)
      }
    }

    const result: DeploymentFrequency[] = []
    for (const [period, count] of frequencyMap.entries()) {
      const [year, month] = period.split("-").map(Number)
      const daysInMonth = new Date(year, month, 0).getDate()

      result.push({
        period,
        count,
        deploymentsPerDay: count / daysInMonth,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average deployment frequency metrics
   * Phase 4: Uses pre-calculated metrics if available
   */
  get averageDeploymentFrequency(): {
    perDay: number
    perWeek: number
    perMonth: number
  } {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics

      if (metrics.length === 0) {
        return { perDay: 0, perWeek: 0, perMonth: 0 }
      }

      // Calculate average deployments per day from all filtered metrics
      let totalDeployments = 0
      let totalDays = 0

      for (const metric of metrics) {
        totalDeployments += metric.deployment_count
        // Calculate days in this period
        const days = Math.ceil((metric.period_end - metric.period_start) / (60 * 60 * 24))
        totalDays += days
      }

      if (totalDays === 0) {
        return { perDay: 0, perWeek: 0, perMonth: 0 }
      }

      const perDay = totalDeployments / totalDays

      return {
        perDay,
        perWeek: perDay * 7,
        perMonth: perDay * 30,
      }
    }

    // Fallback: Use legacy data
    const totalDays = Math.ceil(
      (this.endDate.getTime() - this.startDate.getTime()) / (1000 * 60 * 60 * 24),
    )

    // Prefer deployment data (Phase 2) over releases
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0
    const totalDeployments = useDeployments
      ? this.filteredDeployments.length
      : this.filteredReleases.length

    return {
      perDay: totalDeployments / totalDays,
      perWeek: (totalDeployments / totalDays) * 7,
      perMonth: (totalDeployments / totalDays) * 30,
    }
  }

  /**
   * Calculate lead time for changes
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2/PR timeline
   *
   * Note: When multiple repositories are tracked, metrics are aggregated by period.
   * Lead time values are weighted by sample count when computing averages across repos.
   */
  get leadTimeForChanges(): LeadTimeMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const periodMetrics = metrics.filter((m) => m.lead_time_samples > 0)

      if (periodMetrics.length > 0) {
        // Aggregate metrics by period across all repos
        const periodMap = new Map<
          string,
          {
            totalWeightedAvg: number
            totalWeightedMedian: number
            totalWeightedP95: number
            totalSamples: number
          }
        >()

        for (const metric of periodMetrics) {
          const date = new Date(metric.period_start * 1000)
          const period = this.formatPeriodKey(date, metric.period_type)

          const existing = periodMap.get(period) || {
            totalWeightedAvg: 0,
            totalWeightedMedian: 0,
            totalWeightedP95: 0,
            totalSamples: 0,
          }

          // Weight by sample count for accurate aggregation
          const samples = metric.lead_time_samples || 0
          periodMap.set(period, {
            totalWeightedAvg:
              existing.totalWeightedAvg + (metric.lead_time_avg_hours || 0) * samples,
            totalWeightedMedian:
              existing.totalWeightedMedian + (metric.lead_time_median_hours || 0) * samples,
            totalWeightedP95:
              existing.totalWeightedP95 + (metric.lead_time_p95_hours || 0) * samples,
            totalSamples: existing.totalSamples + samples,
          })
        }

        // Convert map to sorted array with weighted averages
        return Array.from(periodMap.entries())
          .map(([period, data]) => ({
            period,
            averageHours: data.totalSamples > 0 ? data.totalWeightedAvg / data.totalSamples : 0,
            medianHours: data.totalSamples > 0 ? data.totalWeightedMedian / data.totalSamples : 0,
            p95Hours: data.totalSamples > 0 ? data.totalWeightedP95 / data.totalSamples : 0,
          }))
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const leadTimeMap = new Map<string, number[]>()

    // Prefer deployment-based lead time (Phase 2)
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Calculate lead time from commit to deployment
      for (const deployment of this.filteredDeployments) {
        // Find the commit for this deployment
        const commit = this.commits.find(
          (c) => c.hash === deployment.commit_sha && c.repo_id === deployment.repo_id,
        )

        if (commit) {
          // Lead time = deployment time - commit time
          const leadTimeHours = (deployment.created_at - commit.timestamp) / 3600

          const date = new Date(deployment.created_at * 1000)
          const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

          if (!leadTimeMap.has(period)) {
            leadTimeMap.set(period, [])
          }
          // Only count positive lead times (deployment after commit)
          if (leadTimeHours >= 0) {
            leadTimeMap.get(period)?.push(leadTimeHours)
          }
        }
      }
    } else {
      // Fallback: Use PR creation to merge time
      for (const pr of this.filteredPullRequests) {
        if (!pr.merged_at) continue

        const leadTimeHours = (pr.merged_at - pr.created_at) / 3600
        const date = new Date(pr.merged_at * 1000)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!leadTimeMap.has(period)) {
          leadTimeMap.set(period, [])
        }
        leadTimeMap.get(period)?.push(leadTimeHours)
      }
    }

    const result: LeadTimeMetric[] = []
    for (const [period, times] of leadTimeMap.entries()) {
      if (times.length === 0) continue

      times.sort((a, b) => a - b)
      const avg = times.reduce((sum, t) => sum + t, 0) / times.length
      const median = times[Math.floor(times.length / 2)]
      const p95 = times[Math.floor(times.length * 0.95)]

      result.push({
        period,
        averageHours: avg,
        medianHours: median,
        p95Hours: p95,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average lead time
   * Phase 2: Uses commit → deployment when available
   */
  get averageLeadTime(): {
    hours: number
    days: number
  } {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics.filter((m) => m.lead_time_samples > 0)

      if (metrics.length === 0) {
        return { hours: 0, days: 0 }
      }

      // Calculate weighted average based on sample counts
      let totalWeightedHours = 0
      let totalSamples = 0

      for (const metric of metrics) {
        if (metric.lead_time_avg_hours !== undefined && metric.lead_time_samples > 0) {
          totalWeightedHours += metric.lead_time_avg_hours * metric.lead_time_samples
          totalSamples += metric.lead_time_samples
        }
      }

      if (totalSamples === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalWeightedHours / totalSamples

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }

    // Fallback: Use legacy data
    const useDeployments = this.deployments.length > 0 && this.filteredDeployments.length > 0

    if (useDeployments) {
      // Phase 2: Calculate from commit to deployment
      let totalLeadTimeHours = 0
      let count = 0

      for (const deployment of this.filteredDeployments) {
        const commit = this.commits.find(
          (c) => c.hash === deployment.commit_sha && c.repo_id === deployment.repo_id,
        )

        if (commit) {
          const leadTimeHours = (deployment.created_at - commit.timestamp) / 3600
          if (leadTimeHours >= 0) {
            totalLeadTimeHours += leadTimeHours
            count++
          }
        }
      }

      if (count === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalLeadTimeHours / count
      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    } else {
      // Fallback: Use PR timeline
      const mergedPRs = this.filteredPullRequests.filter((pr) => pr.merged_at)
      if (mergedPRs.length === 0) {
        return { hours: 0, days: 0 }
      }

      const totalLeadTimeHours = mergedPRs.reduce((sum, pr) => {
        if (!pr.merged_at) return sum
        const leadTimeHours = (pr.merged_at - pr.created_at) / 3600
        return sum + leadTimeHours
      }, 0)

      const avgHours = totalLeadTimeHours / mergedPRs.length

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }
  }

  /**
   * Calculate change failure rate
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 2 & 3
   *
   * Note: When multiple repositories are tracked, metrics are aggregated by period.
   * Failure rates are computed from total deployments and failures across all repos.
   */
  get changeFailureRate(): ChangeFailureMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const periodMetrics = metrics.filter(
        (m) => m.total_deployments !== undefined && m.total_deployments > 0,
      )

      if (periodMetrics.length > 0) {
        // Aggregate metrics by period across all repos
        const periodMap = new Map<string, { totalDeployments: number; failedDeployments: number }>()

        for (const metric of periodMetrics) {
          const date = new Date(metric.period_start * 1000)
          const period = this.formatPeriodKey(date, metric.period_type)

          const existing = periodMap.get(period) || { totalDeployments: 0, failedDeployments: 0 }

          periodMap.set(period, {
            totalDeployments: existing.totalDeployments + (metric.total_deployments || 0),
            failedDeployments: existing.failedDeployments + (metric.failed_deployments || 0),
          })
        }

        // Convert map to sorted array with calculated failure rates
        return Array.from(periodMap.entries())
          .map(([period, data]) => ({
            period,
            totalDeployments: data.totalDeployments,
            failedDeployments: data.failedDeployments,
            failureRate:
              data.totalDeployments > 0
                ? (data.failedDeployments / data.totalDeployments) * 100
                : 0,
          }))
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const metricsMap = new Map<string, { total: number; failed: number }>()

    // Prefer Phase 2/3 data: deployment failures and incidents
    const useAccurateData =
      (this.deployments.length > 0 && this.deploymentStatuses.length > 0) ||
      this.filteredIncidents.length > 0

    if (useAccurateData) {
      // Phase 2 & 3: Use actual deployment failures and incidents
      // Group deployments by month
      const deploymentsByMonth = new Map<string, DeploymentData[]>()
      for (const deployment of this.filteredDeployments) {
        const timestamp = deployment.created_at * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!deploymentsByMonth.has(period)) {
          deploymentsByMonth.set(period, [])
        }
        deploymentsByMonth.get(period)?.push(deployment)
      }

      // Count failures for each month
      for (const [period, deployments] of deploymentsByMonth.entries()) {
        let failedCount = 0

        for (const deployment of deployments) {
          const status = this.getDeploymentStatus(deployment.id)

          // Check if deployment failed OR caused an incident
          const deploymentFailed = status === "failure"
          const causedIncident = this.filteredIncidents.some((incident) => {
            if (!incident.incident_detected_at) return false

            // Check if incident was detected within 24 hours of deployment
            const incidentTime = incident.incident_detected_at
            const deploymentTime = deployment.created_at
            const timeDiff = incidentTime - deploymentTime

            return (
              timeDiff >= 0 &&
              timeDiff <= 24 * 60 * 60 && // Within 24 hours
              incident.repo_id === deployment.repo_id
            )
          })

          if (deploymentFailed || causedIncident) {
            failedCount++
          }
        }

        metricsMap.set(period, {
          total: deployments.length,
          failed: failedCount,
        })
      }
    } else {
      // Fallback: Use bugfix/revert commits after releases (estimation)
      const releasesByMonth = new Map<string, ReleaseData[]>()
      for (const release of this.filteredReleases) {
        const timestamp = (release.published_at || release.created_at) * 1000
        const date = new Date(timestamp)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!releasesByMonth.has(period)) {
          releasesByMonth.set(period, [])
        }
        releasesByMonth.get(period)?.push(release)
      }

      for (const [period, releases] of releasesByMonth.entries()) {
        let failedCount = 0

        for (const release of releases) {
          const releaseTimestamp = release.published_at || release.created_at
          const windowEnd = releaseTimestamp + 7 * 24 * 60 * 60

          const hasFailure = this.commits.some(
            (commit) =>
              commit.repo_id === release.repo_id &&
              commit.timestamp > releaseTimestamp &&
              commit.timestamp <= windowEnd &&
              (commit.commit_type === "bugfix" || commit.commit_type === "revert"),
          )

          if (hasFailure) {
            failedCount++
          }
        }

        metricsMap.set(period, {
          total: releases.length,
          failed: failedCount,
        })
      }
    }

    const result: ChangeFailureMetric[] = []
    for (const [period, { total, failed }] of metricsMap.entries()) {
      result.push({
        period,
        totalDeployments: total,
        failedDeployments: failed,
        failureRate: total > 0 ? (failed / total) * 100 : 0,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average change failure rate
   * Phase 2 & 3: Uses deployment failures and incidents
   */
  get averageChangeFailureRate(): number {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics.filter(
        (m) => m.total_deployments !== undefined && m.total_deployments > 0,
      )

      if (metrics.length === 0) {
        return 0
      }

      // Calculate weighted average based on total deployments
      let totalFailedDeployments = 0
      let totalDeployments = 0

      for (const metric of metrics) {
        if (
          metric.total_deployments !== undefined &&
          metric.failed_deployments !== undefined &&
          metric.total_deployments > 0
        ) {
          totalFailedDeployments += metric.failed_deployments
          totalDeployments += metric.total_deployments
        }
      }

      if (totalDeployments === 0) {
        return 0
      }

      return (totalFailedDeployments / totalDeployments) * 100
    }

    // Fallback: Use legacy data
    const useAccurateData =
      (this.deployments.length > 0 && this.deploymentStatuses.length > 0) ||
      this.filteredIncidents.length > 0

    if (useAccurateData) {
      // Phase 2 & 3: Use actual failures
      const totalDeployments = this.filteredDeployments.length
      if (totalDeployments === 0) return 0

      let failedCount = 0

      for (const deployment of this.filteredDeployments) {
        const status = this.getDeploymentStatus(deployment.id)
        const deploymentFailed = status === "failure"
        const causedIncident = this.filteredIncidents.some((incident) => {
          if (!incident.incident_detected_at) return false

          const incidentTime = incident.incident_detected_at
          const deploymentTime = deployment.created_at
          const timeDiff = incidentTime - deploymentTime

          return (
            timeDiff >= 0 && timeDiff <= 24 * 60 * 60 && incident.repo_id === deployment.repo_id
          )
        })

        if (deploymentFailed || causedIncident) {
          failedCount++
        }
      }

      return (failedCount / totalDeployments) * 100
    } else {
      // Fallback: Use bugfix/revert estimation
      const totalReleases = this.filteredReleases.length
      if (totalReleases === 0) return 0

      let failedCount = 0

      for (const release of this.filteredReleases) {
        const releaseTimestamp = release.published_at || release.created_at
        const windowEnd = releaseTimestamp + 7 * 24 * 60 * 60

        const hasFailure = this.commits.some(
          (commit) =>
            commit.repo_id === release.repo_id &&
            commit.timestamp > releaseTimestamp &&
            commit.timestamp <= windowEnd &&
            (commit.commit_type === "bugfix" || commit.commit_type === "revert"),
        )

        if (hasFailure) {
          failedCount++
        }
      }

      return (failedCount / totalReleases) * 100
    }
  }

  /**
   * Calculate time to restore service
   * Phase 4: Uses pre-calculated metrics if available, falls back to Phase 3
   *
   * Note: When multiple repositories are tracked, metrics are aggregated by period.
   * MTTR values are weighted by incident count when computing averages across repos.
   */
  get timeToRestore(): TimeToRestoreMetric[] {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics
      const periodMetrics = metrics.filter(
        (m) => m.incident_count !== undefined && m.incident_count > 0,
      )

      if (periodMetrics.length > 0) {
        // Aggregate metrics by period across all repos
        const periodMap = new Map<
          string,
          {
            totalWeightedAvg: number
            totalWeightedMedian: number
            totalIncidents: number
          }
        >()

        for (const metric of periodMetrics) {
          const date = new Date(metric.period_start * 1000)
          const period = this.formatPeriodKey(date, metric.period_type)

          const existing = periodMap.get(period) || {
            totalWeightedAvg: 0,
            totalWeightedMedian: 0,
            totalIncidents: 0,
          }

          // Weight by incident count for accurate aggregation
          const incidents = metric.incident_count || 0
          periodMap.set(period, {
            totalWeightedAvg: existing.totalWeightedAvg + (metric.mttr_avg_hours || 0) * incidents,
            totalWeightedMedian:
              existing.totalWeightedMedian + (metric.mttr_median_hours || 0) * incidents,
            totalIncidents: existing.totalIncidents + incidents,
          })
        }

        // Convert map to sorted array with weighted averages
        return Array.from(periodMap.entries())
          .map(([period, data]) => ({
            period,
            averageHours: data.totalIncidents > 0 ? data.totalWeightedAvg / data.totalIncidents : 0,
            medianHours:
              data.totalIncidents > 0 ? data.totalWeightedMedian / data.totalIncidents : 0,
            incidents: data.totalIncidents,
          }))
          .sort((a, b) => a.period.localeCompare(b.period))
      }
    }

    // Fallback: Calculate from raw data
    const metricsMap = new Map<string, number[]>()

    // Prefer Phase 3 incident data
    const useIncidents = this.filteredIncidents.length > 0

    if (useIncidents) {
      // Phase 3: Use actual incident detected → resolved time
      for (const incident of this.filteredIncidents) {
        if (!incident.incident_detected_at || !incident.incident_resolved_at) {
          continue // Skip unresolved or incomplete incidents
        }

        const restoreTimeHours =
          (incident.incident_resolved_at - incident.incident_detected_at) / 3600

        const date = new Date(incident.incident_detected_at * 1000)
        const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

        if (!metricsMap.has(period)) {
          metricsMap.set(period, [])
        }
        // Only count positive restore times
        if (restoreTimeHours >= 0) {
          metricsMap.get(period)?.push(restoreTimeHours)
        }
      }
    } else {
      // Fallback: Estimate from bugfix/revert commits (rough approximation)
      const failureCommits = this.filteredCommits.filter(
        (c) => c.commit_type === "bugfix" || c.commit_type === "revert",
      )

      for (const failure of failureCommits) {
        const precedingRelease = this.releases
          .filter(
            (r) =>
              r.repo_id === failure.repo_id && (r.published_at || r.created_at) < failure.timestamp,
          )
          .sort((a, b) => {
            const aTime = a.published_at || a.created_at
            const bTime = b.published_at || b.created_at
            return bTime - aTime
          })[0]

        if (precedingRelease) {
          const releaseTime = precedingRelease.published_at || precedingRelease.created_at
          const restoreTimeHours = (failure.timestamp - releaseTime) / 3600

          const date = new Date(failure.timestamp * 1000)
          const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`

          if (!metricsMap.has(period)) {
            metricsMap.set(period, [])
          }
          metricsMap.get(period)?.push(restoreTimeHours)
        }
      }
    }

    const result: TimeToRestoreMetric[] = []
    for (const [period, times] of metricsMap.entries()) {
      if (times.length === 0) continue

      times.sort((a, b) => a - b)
      const avg = times.reduce((sum, t) => sum + t, 0) / times.length
      const median = times[Math.floor(times.length / 2)]

      result.push({
        period,
        averageHours: avg,
        medianHours: median,
        incidents: times.length,
      })
    }

    return result.sort((a, b) => a.period.localeCompare(b.period))
  }

  /**
   * Calculate average time to restore
   * Phase 3: Uses actual incident data when available
   */
  get averageTimeToRestore(): {
    hours: number
    days: number
  } {
    // Phase 4: Use pre-calculated metrics if available
    if (this.hasPreCalculatedMetrics) {
      const metrics = this.filteredDoraMetrics.filter(
        (m) => m.incident_count !== undefined && m.incident_count > 0,
      )

      if (metrics.length === 0) {
        return { hours: 0, days: 0 }
      }

      // Calculate weighted average based on incident counts
      let totalWeightedHours = 0
      let totalIncidents = 0

      for (const metric of metrics) {
        if (
          metric.mttr_avg_hours !== undefined &&
          metric.incident_count !== undefined &&
          metric.incident_count > 0
        ) {
          totalWeightedHours += metric.mttr_avg_hours * metric.incident_count
          totalIncidents += metric.incident_count
        }
      }

      if (totalIncidents === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalWeightedHours / totalIncidents

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }

    // Fallback: Use legacy data
    const useIncidents = this.filteredIncidents.length > 0

    if (useIncidents) {
      // Phase 3: Use actual incident restore times
      const resolvedIncidents = this.filteredIncidents.filter(
        (i) => i.incident_detected_at && i.incident_resolved_at,
      )

      if (resolvedIncidents.length === 0) {
        return { hours: 0, days: 0 }
      }

      const totalRestoreTime = resolvedIncidents.reduce((sum, incident) => {
        if (!incident.incident_detected_at || !incident.incident_resolved_at) {
          return sum
        }
        return sum + (incident.incident_resolved_at - incident.incident_detected_at)
      }, 0)

      const avgHours = totalRestoreTime / resolvedIncidents.length / 3600

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    } else {
      // Fallback: Estimate from bugfix/revert commits
      const failureCommits = this.filteredCommits.filter(
        (c) => c.commit_type === "bugfix" || c.commit_type === "revert",
      )

      if (failureCommits.length === 0) {
        return { hours: 0, days: 0 }
      }

      let totalRestoreTime = 0
      let count = 0

      for (const failure of failureCommits) {
        const precedingRelease = this.releases
          .filter(
            (r) =>
              r.repo_id === failure.repo_id && (r.published_at || r.created_at) < failure.timestamp,
          )
          .sort((a, b) => {
            const aTime = a.published_at || a.created_at
            const bTime = b.published_at || b.created_at
            return bTime - aTime
          })[0]

        if (precedingRelease) {
          const releaseTime = precedingRelease.published_at || precedingRelease.created_at
          totalRestoreTime += failure.timestamp - releaseTime
          count++
        }
      }

      if (count === 0) {
        return { hours: 0, days: 0 }
      }

      const avgHours = totalRestoreTime / count / 3600

      return {
        hours: avgHours,
        days: avgHours / 24,
      }
    }
  }

  /**
   * Determine DORA performance level based on metrics
   * Based on 2024 DORA benchmarks
   */
  get performanceLevel(): DoraPerformanceLevel {
    const deployFreq = this.averageDeploymentFrequency.perDay
    const leadTime = this.averageLeadTime.hours
    const failureRate = this.averageChangeFailureRate
    const restoreTime = this.averageTimeToRestore.hours

    // Deployment Frequency
    let deploymentLevel: DoraPerformanceLevel["deploymentFrequency"]
    if (deployFreq >= 1) {
      deploymentLevel = "elite" // Multiple deploys per day
    } else if (deployFreq >= 1 / 7) {
      deploymentLevel = "high" // Once per week
    } else if (deployFreq >= 1 / 30) {
      deploymentLevel = "medium" // Once per month
    } else {
      deploymentLevel = "low" // Less than once per month
    }

    // Lead Time for Changes
    let leadTimeLevel: DoraPerformanceLevel["leadTime"]
    if (leadTime <= 24) {
      leadTimeLevel = "elite" // Less than one day
    } else if (leadTime <= 24 * 7) {
      leadTimeLevel = "high" // Less than one week
    } else if (leadTime <= 24 * 30) {
      leadTimeLevel = "medium" // Less than one month
    } else {
      leadTimeLevel = "low" // More than one month
    }

    // Change Failure Rate
    let failureRateLevel: DoraPerformanceLevel["changeFailureRate"]
    if (failureRate <= 5) {
      failureRateLevel = "elite" // 0-5%
    } else if (failureRate <= 10) {
      failureRateLevel = "high" // 5-10%
    } else if (failureRate <= 15) {
      failureRateLevel = "medium" // 10-15%
    } else {
      failureRateLevel = "low" // >15%
    }

    // Time to Restore Service
    let restoreTimeLevel: DoraPerformanceLevel["timeToRestore"]
    if (restoreTime <= 1) {
      restoreTimeLevel = "elite" // Less than one hour
    } else if (restoreTime <= 24) {
      restoreTimeLevel = "high" // Less than one day
    } else if (restoreTime <= 24 * 7) {
      restoreTimeLevel = "medium" // Less than one week
    } else {
      restoreTimeLevel = "low" // More than one week
    }

    // Overall level (most conservative)
    const levels = [deploymentLevel, leadTimeLevel, failureRateLevel, restoreTimeLevel]
    const levelScores = { elite: 4, high: 3, medium: 2, low: 1 }
    const avgScore = levels.reduce((sum, level) => sum + levelScores[level], 0) / levels.length

    let overall: DoraPerformanceLevel["overall"]
    if (avgScore >= 3.5) {
      overall = "elite"
    } else if (avgScore >= 2.5) {
      overall = "high"
    } else if (avgScore >= 1.5) {
      overall = "medium"
    } else {
      overall = "low"
    }

    return {
      deploymentFrequency: deploymentLevel,
      leadTime: leadTimeLevel,
      changeFailureRate: failureRateLevel,
      timeToRestore: restoreTimeLevel,
      overall,
    }
  }

  /**
   * Check if DORA metrics data is available
   * Now checks for pre-calculated metrics as the primary source
   */
  get hasData(): boolean {
    return this.doraMetrics.length > 0
  }

  /**
   * Get data completeness information
   * Updated to work with pre-calculated metrics from dora_metrics.json
   *
   * Note: This method now infers data availability from pre-calculated metrics rather than
   * checking raw data files. The return type maintains backward compatibility with existing
   * consumers that expect flags like hasReleases, hasPullRequests, etc.
   */
  get dataCompleteness(): {
    hasReleases: boolean
    hasPullRequests: boolean
    hasCommits: boolean
    hasDeployments: boolean
    hasWorkflowRuns: boolean
    hasIncidents: boolean
    missingDataPoints: DoraMissingDataKey[]
    dataQuality: "accurate" | "estimated" | "incomplete"
  } {
    // When using pre-calculated metrics, data completeness is determined
    // by what metrics are present in dora_metrics.json
    const hasDoraMetrics = this.doraMetrics.length > 0

    // Check what's available in the pre-calculated metrics
    // Deployment Frequency metrics: deployment_count > 0 indicates releases/deployments data was used
    const hasDeploymentMetrics = this.doraMetrics.some((m) => m.deployment_count > 0)

    // Lead Time metrics: lead_time_samples > 0 indicates PR/commit data was used for calculations
    const hasLeadTimeMetrics = this.doraMetrics.some((m) => m.lead_time_samples > 0)

    // Change Failure Rate metrics: requires total_deployments and failure tracking data
    // Check for both total_deployments and failure_rate_percent to ensure complete data
    const hasFailureRateMetrics = this.doraMetrics.some(
      (m) =>
        m.total_deployments !== undefined &&
        m.total_deployments > 0 &&
        m.failure_rate_percent !== undefined,
    )

    // Time to Restore metrics: incident_count > 0 indicates incident tracking data was used
    const hasTimeToRestoreMetrics = this.doraMetrics.some(
      (m) => m.incident_count !== undefined && m.incident_count > 0,
    )

    const missingDataPoints: DoraMissingDataKey[] = []

    // Map pre-calculated metrics availability to missing raw data points.
    // This mapping reflects which raw data sources are typically needed to calculate each metric:
    // - Deployment Frequency: releases.json or deployments.json
    // - Lead Time: pull_requests.json and commits.json
    // - Change Failure Rate: workflow_runs.json (for CI/CD failure data)
    // - Time to Restore: issues.json (with incident labels)
    if (!hasDeploymentMetrics) {
      missingDataPoints.push("doraMissingReleases")
      missingDataPoints.push("doraMissingDeployments")
    }
    if (!hasLeadTimeMetrics) {
      missingDataPoints.push("doraMissingPullRequests")
      missingDataPoints.push("doraMissingCommits")
    }
    if (!hasFailureRateMetrics) {
      missingDataPoints.push("doraMissingWorkflowRuns")
    }
    if (!hasTimeToRestoreMetrics) {
      missingDataPoints.push("doraMissingIncidents")
    }

    // Determine data quality level based on pre-calculated metrics availability
    let dataQuality: "accurate" | "estimated" | "incomplete"
    if (hasDoraMetrics && hasDeploymentMetrics && hasLeadTimeMetrics) {
      // If we have pre-calculated metrics with deployment and lead time data
      if (hasFailureRateMetrics && hasTimeToRestoreMetrics) {
        dataQuality = "accurate" // Full DORA metrics available
      } else {
        dataQuality = "estimated" // Core metrics available
      }
    } else {
      dataQuality = "incomplete" // Missing core data
    }

    // Return structure maintains backward compatibility with existing consumers.
    // Note: These flags now indicate metric availability rather than raw data availability.
    // For example, hasReleases=true means deployment frequency metrics are available,
    // not that releases.json was loaded directly.
    return {
      hasReleases: hasDeploymentMetrics,
      hasPullRequests: hasLeadTimeMetrics,
      hasCommits: hasLeadTimeMetrics,
      hasDeployments: hasDeploymentMetrics,
      hasWorkflowRuns: hasFailureRateMetrics,
      hasIncidents: hasTimeToRestoreMetrics,
      missingDataPoints,
      dataQuality,
    }
  }
}
